import { useField, useFormikContext } from "formik";
import { Box, Flex, Text } from "@radix-ui/themes";
import InputField from "@/components/form/InputField";
import SearchableSelectField from "@/components/form/SearchableSelectField";
import FilterConditionsField from "./shared/FilterConditionsField";
import { FieldSelector } from "@/components/field-selector/FieldSelector";
import { useEntities } from "@/hooks/useCommonQueries";
import { useQuery } from "@tanstack/react-query";
import { anyService } from "@/services/breeze/AnyService";
import { EntityFieldTree } from "@/types/breeze/Entity";
import { object, string, array, number, InferType } from "yup";
import { useMemo } from "react";

export const readDataValidationSchema = object({
  parameters: object().shape({
    entity: string().required("Entity is required"),
    filter_conditions: array().of(
      object().shape({
        field: string().required("Field is required"),
        operator: string().required("Operator is required"),
        value: string().when("operator", {
          is: (op: string) => op !== "is_null" && op !== "is_not_null",
          then: (schema) => schema.required("Value is required"),
          otherwise: (schema) => schema.notRequired(),
        }),
      })
    ),
    limit: number().required("Limit is required").min(1, "Minimum limit is 1"),
    fields: string().optional(),
    sort_by: string().optional(),
    sort_direction: string().optional(),
  }),
});

export type ReadDataFormValues = InferType<typeof readDataValidationSchema>;

export default function ReadDataFields() {
  const { values } = useFormikContext<ReadDataFormValues>();
  const { data: entities } = useEntities();

  if (!values.parameters.filter_conditions) {
    values.parameters.filter_conditions = [];
  }

  const entityOptions = useMemo(() =>
    entities?.map((e) => ({ value: e.name, label: e.name })) || [], [entities]);

  const selectedEntity = entities?.find((e) => e.name === values.parameters.entity);
  const fieldOptions = useMemo(() =>
    selectedEntity?.fields?.map((f) => ({ value: f.name, label: f.label || f.name })) || [], [selectedEntity]);

  const sortField = selectedEntity?.fields?.find((f) => f.name === values.parameters.sort_by);
  const directionOptions = useMemo(() => {
    switch (sortField?.database_type) {
      case "date":
      case "timestamp":
        return [
          { value: "asc", label: "Ascending — oldest first" },
          { value: "desc", label: "Descending — newest first" },
        ];
      case "integer":
      case "bigint":
      case "decimal":
        return [
          { value: "asc", label: "Ascending — smallest first" },
          { value: "desc", label: "Descending — largest first" },
        ];
      case "varchar":
      case "text":
        return [
          { value: "asc", label: "Ascending — A → Z" },
          { value: "desc", label: "Descending — Z → A" },
        ];
      default:
        return [
          { value: "asc", label: "Ascending" },
          { value: "desc", label: "Descending" },
        ];
    }
  }, [sortField]);

  return (
    <Flex direction="column" gap="6">
      <SearchableSelectField
        name="parameters.entity"
        label="Entity"
        required
        options={entityOptions}
        searchPlaceholder="Search entities..."
        placeholder="Select entity..."
      />

      <FilterConditionsField
        name="parameters.filter_conditions"
        entityName={values.parameters.entity}
      />

      <InputField
        name="parameters.limit"
        label="Limit"
        type="number"
        required
      />

      <FieldsPicker entityName={values.parameters.entity} />

      <Flex gap="4">
        <SearchableSelectField
          name="parameters.sort_by"
          label="Sort By"
          options={fieldOptions}
          placeholder="Select field..."
          searchPlaceholder="Search fields..."
        />

        <SearchableSelectField
          name="parameters.sort_direction"
          label="Sort Direction"
          options={directionOptions}
          placeholder="Ascending"
        />
      </Flex>
    </Flex>
  );
}

function FieldsPicker({ entityName }: { entityName: string | undefined }) {
  const [field, , helpers] = useField<string | undefined>("parameters.fields");

  const { data: treeData = [] } = useQuery<EntityFieldTree[]>({
    queryKey: ["entity-hierarchy", entityName],
    queryFn: () => anyService.hierarchy(entityName!),
    enabled: !!entityName,
    staleTime: 5 * 60 * 1000,
  });

  const selected = useMemo(
    () => (field.value ?? "").split(",").map((s) => s.trim()).filter(Boolean),
    [field.value],
  );

  return (
    <Box>
      <Text as="label" size="2" weight="medium" mb="1" style={{ display: "block" }}>
        Fields
      </Text>
      <FieldSelector
        value={selected}
        onChange={(next) => helpers.setValue(next.join(","))}
        treeData={treeData}
        placeholder="All fields"
      />
      <Text size="1" color="gray" mt="1" style={{ display: "block" }}>
        Leave empty to include all fields in the response.
      </Text>
    </Box>
  );
}
